import * as elog from '@reverbdotcom/commons/src/elog';
import { CreateImageUploadUrlMutation, CreateImageUploadUrlMutationVariables } from '../../gql/graphql';
import { gql } from '@reverbdotcom/commons/src/gql';
import { MutationFunction, ApolloClient } from '@apollo/client';
import { mapS3PhotoToCloudinaryResponse } from './s3Photo';

export const CREATE_IMAGE_UPLOAD_URL_MUTATION = gql(`
  mutation CreateImageUploadUrl($input: CreateImageUploadUrlInput!) {
    createImageUploadUrl(input: $input) {
      uploadUrl
    }
  }
`);

/**
 * Successful file uploads resolve with `ICloudinaryResponse`, which may be from Cloudinary's API,
 * or data from direct s3 uploads that is mapped to `ICloudinaryResponse`.
 *
 * Unsuccessful file uploads use `Promise.resolve()` which is why `void` is here.
 * It might be a better choice to resolve with null, or undefined, or use `Promise.reject()` for
 * upload failures, but void was used for backwards compatibility with existing code.
 */
export type UploadFileResult = ICloudinaryResponse | void;

export interface ICloudinaryResponse {
  public_id: string;
  version: number;
  width: number;
  height: number;
  format: string;
  resource_type: string;
  original_filename?: string;
}

export async function uploadFile(
  file: File,
  createImageUploadUrlMutation: MutationFunction<CreateImageUploadUrlMutation, CreateImageUploadUrlMutationVariables>,
  uploadingToS3: boolean,
  retries = 1,
): Promise<UploadFileResult> {
  try {
    const imageUploadUrlResult = await createImageUploadUrlMutation({
      variables: {
        input: {
          originalFilename: file.name,
        },
      },
    });

    if (imageUploadUrlResult.errors?.length) throw new Error('Failed to get signed image upload URL');

    const uploadUrl = imageUploadUrlResult.data.createImageUploadUrl.uploadUrl;

    if (uploadingToS3) {
      const uploadResult = await fetch(
        uploadUrl, {
          method: 'put',
          body: file,
          headers: {
            'Content-Type': file.type,
          },
        },
      );

      if (!uploadResult.ok) throw new Error(`Image upload failed with status ${uploadResult.status}`);

      return await mapS3PhotoToCloudinaryResponse(uploadUrl, file);
    } else {
      // Cloudinary upload
      const uploadData = new FormData();
      uploadData.append('file', file);

      const result = await fetch(
        imageUploadUrlResult.data.createImageUploadUrl.uploadUrl, {
          method: 'put',
          body: uploadData,
        },
      );

      return await result.json() as ICloudinaryResponse;
    }
  } catch (e) {
    if (retries > 0) {
      return uploadFile(file, createImageUploadUrlMutation, uploadingToS3, retries - 1);
    } else {
      elog.error('cloudinary.frontend.uploadfile.error', {}, e);
    }

    return Promise.resolve();
  }
}

export function uploadFiles(
  files: File[],
  createImageUploadUrlMutation: MutationFunction<CreateImageUploadUrlMutation, CreateImageUploadUrlMutationVariables>,
  uploadingToS3: boolean,
): Promise<UploadFileResult[]> {
  const requests = files.map((file) => uploadFile(file, createImageUploadUrlMutation, uploadingToS3, 1));

  return Promise.all(requests);
}

/**
 * Do not use this.
 */
export async function createImageUploadUrlOutsideReact(
  fileName: string,
  apolloClient: ApolloClient<object>,
): Promise<string> {
  const imageUploadUrlResult = await apolloClient.mutate({
    mutation: CREATE_IMAGE_UPLOAD_URL_MUTATION,
    variables: {
      input: {
        originalFilename: fileName,
      },
    },
  });

  if (imageUploadUrlResult.errors?.length) {
    throw new Error('Failed to get signed image upload URL');
  }

  return imageUploadUrlResult.data.createImageUploadUrl.uploadUrl;
}
